import { Observable, from } from 'rxjs';
import { TranslateLoader } from '@ngx-translate/core';
import * as cacheBusting from '../i18n-cache-busting.json';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';

// Type for translation content
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationObject = Record<string, any>;

// Type for Node.js require function in server context
type NodeRequire = (id: string) => any; // eslint-disable-line @typescript-eslint/no-explicit-any

export class TranslateServerLoader implements TranslateLoader {
	constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {}

	public getTranslation(lang: string): Observable<TranslationObject> {
		// During server-side rendering, read files directly from filesystem
		if (isPlatformServer(this.platformId)) {
			return from(this.loadFromFileSystem(lang));
		}

		// For browser, use HTTP requests - always use relative path to avoid CORS issues
		const path = `/assets/i18n/${lang}.json?v=${cacheBusting[lang]}`;
		return this.http.get<TranslationObject>(path);
	}

	private async loadFromFileSystem(lang: string): Promise<TranslationObject> {
		try {
			const nodeRequire = this.getNodeRequire();
			if (!nodeRequire) {
				return {};
			}

			// Use runtime require indirection to avoid bundling Node built-ins on browser builds
			const fs = nodeRequire('fs');
			const path = nodeRequire('path');
			const process = nodeRequire('process');

			// Try to read from the dist folder first
			const distPath = path.join(process.cwd(), 'dist', 'assets', 'i18n', `${lang}.json`);
			const content = fs.readFileSync(distPath, 'utf8');
			return JSON.parse(content) as TranslationObject;
		} catch (error) {
			try {
				const nodeRequire = this.getNodeRequire();
				if (!nodeRequire) {
					return {};
				}

				const fs = nodeRequire('fs');
				const path = nodeRequire('path');
				const process = nodeRequire('process');

				// Fallback to src folder during development
				const srcPath = path.join(process.cwd(), 'src', 'assets', 'i18n', `${lang}.json`);
				const content = fs.readFileSync(srcPath, 'utf8');
				return JSON.parse(content) as TranslationObject;
			} catch (fallbackError) {
				// In case of any errors, return empty object
				return {};
			}
		}
	}

	private getNodeRequire(): NodeRequire | null {
		try {
			return Function('return require')() as NodeRequire;
		} catch (error) {
			return null;
		}
	}
}

export function translateServerLoaderFactory(http: HttpClient, platformId: Object): TranslateLoader {
	return new TranslateServerLoader(http, platformId);
}
